// The plan a report is generated from (Report v2): the AI reads the analyst's brief and answers with a title, the
// report question (the same structured query the builder edits), a template whose sections fit (or custom
// sections), a few suggested figures, the plan read back in words and what could not be expressed. The person
// checks the plan before the report is created from it. Everything here is checked: ids are never accepted (the
// model knows none), the query goes through the report parser, figures must point at a section of the plan.

import type { DossierFigureKind } from './dossier';
import { DOSSIER_FIGURE_KINDS } from './dossier';
import { cleanCaption, type FigureSpec, parseFigureSpec } from './report-document';
import { parseReportQuery, REPORT_LIMITS, REPORT_TEMPLATE_LIMITS, type ReportQuery } from './reports';
import type { SchemaSnapshot } from './schema/types';

export const REPORT_PLAN_LANGUAGES = ['en', 'fr'] as const;
export type ReportPlanLanguage = (typeof REPORT_PLAN_LANGUAGES)[number];
export const isReportPlanLanguage = (v: unknown): v is ReportPlanLanguage => REPORT_PLAN_LANGUAGES.includes(v as ReportPlanLanguage);

export interface ReportPlanSection {
  key: string;
  title: string;
  guidance: string;
  starter?: ReportQuery | null;
}

export interface ReportPlanFigure {
  kind: DossierFigureKind;
  /** A section of the plan, or null for a figure before the first section. */
  sectionKey: string | null;
  caption: string;
  spec?: FigureSpec;
}

export interface ReportPlan {
  v: 1;
  title: string;
  language: ReportPlanLanguage;
  /** Validated with parseReportQuery; ids stripped; the same rules as Ask in words (D385). */
  query: ReportQuery;
  /** A template whose sections fit, else null. */
  templateId: string | null;
  /** From the template, or custom. */
  sections: ReportPlanSection[];
  /** Suggestions, at most REPORT_PLAN_LIMITS.figures. */
  figures: ReportPlanFigure[];
  /** The plan read back in the plan's language. */
  inWords: string;
  /** What could not be expressed. */
  dropped: string[];
}

export const REPORT_PLAN_LIMITS = {
  figures: 6,
  sections: REPORT_TEMPLATE_LIMITS.sections,
  inWordsChars: 4000,
  dropped: 20,
  droppedChars: 160,
} as const;

const isObj = (v: unknown): v is Record<string, unknown> => !!v && typeof v === 'object' && !Array.isArray(v);
const SECTION_KEY = /^[a-z0-9][a-z0-9_-]{0,59}$/;
const TEMPLATE_ID = /^[0-9A-Za-z_-]{1,64}$/;
const oneLine = (s: string, max: number) => s.replace(/\s+/g, ' ').trim().slice(0, max);
const cleanText = (v: unknown, max: number) => (typeof v === 'string' ? oneLine(v, max) : '');

/** Removes every `ids` a model might give (it knows no ids), at any depth. */
export function withoutIds(v: unknown): unknown {
  if (Array.isArray(v)) return v.map(withoutIds);
  if (!isObj(v)) return v;
  const out: Record<string, unknown> = {};
  for (const [k, x] of Object.entries(v)) if (k !== 'ids') out[k] = withoutIds(x);
  return out;
}

/**
 * A plan as the model answered it or as the client sends it back: the query parsed (ids stripped first), the
 * sections with unique keys and at least one of them, figures whose section is not in the plan dropped, specs that
 * don't parse left out, texts cut to their limits. Not a plan → null. With the schema, the query's keys must exist.
 */
export function parseReportPlan(raw: unknown, schema?: SchemaSnapshot): ReportPlan | null {
  if (!isObj(raw)) return null;
  const language = isReportPlanLanguage(raw.language) ? raw.language : 'en';
  const query = parseReportQuery(withoutIds(raw.query), schema);
  if (!query.ok) return null;
  const sections: ReportPlanSection[] = [];
  const keys = new Set<string>();
  for (const s of Array.isArray(raw.sections) ? raw.sections : []) {
    if (!isObj(s) || typeof s.key !== 'string' || !SECTION_KEY.test(s.key) || keys.has(s.key)) continue;
    const title = cleanText(s.title, REPORT_TEMPLATE_LIMITS.title);
    if (!title) continue;
    keys.add(s.key);
    let starter: ReportQuery | null = null;
    if (s.starter !== undefined && s.starter !== null) {
      const st = parseReportQuery(withoutIds(s.starter), schema);
      if (st.ok) starter = st.value;
    }
    sections.push({
      key: s.key,
      title,
      guidance: typeof s.guidance === 'string' ? s.guidance.trim().slice(0, REPORT_TEMPLATE_LIMITS.guidance) : '',
      starter,
    });
    if (sections.length >= REPORT_PLAN_LIMITS.sections) break;
  }
  if (!sections.length) return null;
  const figures: ReportPlanFigure[] = [];
  for (const f of Array.isArray(raw.figures) ? raw.figures : []) {
    if (!isObj(f) || !(DOSSIER_FIGURE_KINDS as readonly unknown[]).includes(f.kind)) continue;
    const sectionKey = f.sectionKey === null || f.sectionKey === undefined ? null : typeof f.sectionKey === 'string' ? f.sectionKey : '';
    if (sectionKey !== null && !keys.has(sectionKey)) continue;
    const spec = f.spec === undefined || f.spec === null ? null : parseFigureSpec(withoutIds(f.spec));
    if (spec && spec.kind !== f.kind) continue;
    figures.push({ kind: f.kind as DossierFigureKind, sectionKey, caption: cleanCaption(f.caption), ...(spec ? { spec } : {}) });
    if (figures.length >= REPORT_PLAN_LIMITS.figures) break;
  }
  const dropped = (Array.isArray(raw.dropped) ? raw.dropped : [])
    .map(d => cleanText(d, REPORT_PLAN_LIMITS.droppedChars))
    .filter(Boolean)
    .slice(0, REPORT_PLAN_LIMITS.dropped);
  return {
    v: 1,
    title: cleanText(raw.title, REPORT_LIMITS.name) || (language === 'fr' ? 'Rapport' : 'Report'),
    language,
    query: query.value,
    templateId: typeof raw.templateId === 'string' && TEMPLATE_ID.test(raw.templateId) ? raw.templateId : null,
    sections,
    figures,
    inWords: typeof raw.inWords === 'string' ? raw.inWords.trim().slice(0, REPORT_PLAN_LIMITS.inWordsChars) : '',
    dropped,
  };
}
